import * as React from 'react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Col, Input, Label, Nav, NavItem, NavLink, Row, TabContent, TabPane } from 'reactstrap';
import Swal from 'sweetalert2';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { Afr, existFactsInTable, FactTable, generateLocalStateFromEventProperties, getSelectedAfr, getTable, saveDocumentRelationship, updateVersionStateValue } from '../../redux/AfrSlice';
import { resetConceptState } from "../../redux/ConceptSlice";
import { DocumentRelationship, uploadFile } from '../../redux/DocumentsSlice';
import { Entity, getSelectedEntity } from '../../redux/EntitySlice';
import { isAdmin, setIsDirty } from '../../redux/MiscSlice';
import enums from '../../utils/enums';
import utilityHelper from '../../utils/utilityHelper';
import RevenuesCoreDetail from '../AfrCore/RevenuesCoreDetail';
import RevenuesCoreImpactFees from '../AfrCore/RevenuesCoreImpactFees';
import Page from '../Page';
import { AdminUserRoleEnum, selectAdminUserRole } from '../../redux/UserSlice';

const RevenuesCore = (props: { userRole: string }) => {

    const dispatch = useAppDispatch();

    /* Tabs logic */
    const [activeTab, setActiveTab] = useState("1");
    const [hasFacts, setHasFacts] = useState(true);
    const [revenueDocumentation, setRevenueDocumentation] = useState<string[]>([]);
    const isAdminUser: boolean = useAppSelector(isAdmin);
    const adminUserRole: AdminUserRoleEnum | null = useAppSelector(selectAdminUserRole);
    const parameters = useLocation();


    const [fileName, setFileName] = useState("");
    const [pdfDocumentId, setPdfDocumentId] = useState("");
    // reference to input type file
    const openFileRef = React.useRef<any>();

    const toggle = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    }

    const activeClass = (tab: string) => {
        return (tab === activeTab) ? "active" : "";
    }

    const currentAfr: Afr | null = useAppSelector(getSelectedAfr);
    const [state, setState] = useState(generateLocalStateFromEventProperties(currentAfr, ["workState_revenuesComplete", "workState_noRevenues", "reviewState_revenuesReviewed"]));

    var settings = {
        upload: { legend: "", entityType: "AFFIDAVIT", actionType: "UPLOAD_PDF", checkboxLegend: "", cardTitle: "Affidavit PDF", successfulUploadMessage: "Affidavit Uploaded Successfully!" },
    }


    const currVersion = function () {
        if (currentAfr) {
            if (currentAfr.currentversion) {
                if (currentAfr.currentversion.versionState) {
                    return currentAfr.currentversion.versionState;
                }
            }
        }
        return null;
    }
    const updateHasFacts = function (bHasFacts: boolean) {
        if (bHasFacts) {
            setHasFacts(true);
            return;
        }
        if (currentAfr) {
            setHasFacts(existFactsInTable(currentAfr, ["uas_020_revenue", "uas_020_revenue_dwellings_and_fees"]));
        } else {
            setHasFacts(false);
        }
    }

    const afrYear = currentAfr ? currentAfr.year : 0;
    const entity: Entity | null = useAppSelector(getSelectedEntity);
    const ename = entity ? entity.name : "";
    const ecode = entity ? entity.code : "";

    const handleChangeCheckbox = function (event : any) {

        var checkValues = JSON.parse(JSON.stringify(state));
        var key: string = event.target.name;
        var value: boolean = event.target.checked
        checkValues[key] = value;

        setState(checkValues);

        // call to dispatcher by sending:
        var eventFirstlevel = key.split("_")[0]
        var eventSecondlevel = key.split("_")[1]

        
        // Update global flag for this event in redux
        dispatch(updateVersionStateValue({ propGroup: eventFirstlevel, propName: eventSecondlevel, boolValue: value, value: null }));

        // set the dirty flag to TRUE
        dispatch(setIsDirty({ IsDirty: true, Page: "" }))
       
        if (key == 'workState_revenuesComplete' && value == true) {
            var acctTable: FactTable | null = null;
            acctTable = getTable(currentAfr, 'uas_020_revenue_dwellings_and_fees');

            if (acctTable) {
                if (acctTable.rows.length > 0) {
                    if (currentAfr) {
                        var impactFeeAffidavitExists: boolean = true;
                        if (!currentAfr.currentversion.documents) {
                            impactFeeAffidavitExists = false;
                        } else {
                            var foundImpactFeeAffidavitDocuments = currentAfr.currentversion.documents.filter((element: DocumentRelationship) => element.role == "IMPACT_FEE_AFFIDAVIT");
                            if (foundImpactFeeAffidavitDocuments.length <= 0) {
                                impactFeeAffidavitExists = false;
                            }
                        }

                        if (impactFeeAffidavitExists == false) {
                            Swal.fire({
                                html: "We noticed you have reported Impact Fees.  <br />Would you like to upload your Impact Fee Affidavit now?  <br/><br/>",
                                icon: 'question',
                                allowOutsideClick: false,
                                showCancelButton: true,
                                showDenyButton: false,
                                confirmButtonColor: '#3085d6',
                                cancelButtonColor: '#d33',
                                confirmButtonText: 'Yes',
                                cancelButtonText: 'No',
                            }).then(async (result: any) => {
                                if (result.isConfirmed) {
                                    openFileRef.current.click();
                                }
                                else {
                                    Swal.fire({
                                        html: "When you are ready to upload your Impact Fee Affidavit, you can do so in the Impact Fee Affidavit tab on the Supplementary Information page or on the Documents tab on the Summary page.",
                                        icon: 'info',
                                        allowOutsideClick: false,
                                        showCancelButton: false,
                                        showDenyButton: false,
                                        confirmButtonColor: '#3085d6',
                                        confirmButtonText: 'Okay',
                                    });
                                }

                            });
                        }
                    }
                }
            }
        }
    }

    const handleUploadFile = async function (uploadedFile: File | undefined) {

        if (uploadedFile !== undefined) {
            let document: DocumentRelationship;
            var now = new Date().toISOString();
            var documentGuid = "";

           
            // Upload file and set back the document id
            documentGuid = await uploadFile(uploadedFile, `${settings.upload.entityType} PDF document`, dispatch);
           
            document = { documentId: documentGuid, role: enums.documentRole.affidavit, timestamp: now, name: uploadedFile.name, affiliate: null }
            
            dispatch(saveDocumentRelationship(document));           

            // Turn ON the dirty flag in redux
            dispatch(setIsDirty({ IsDirty: true, Page: "" }));

            setPdfDocumentId(documentGuid);
            setFileName(uploadedFile.name);

            if (documentGuid.length > 0) {
                Swal.fire(utilityHelper.uploadSuccessfulNotFinished);
            }
        }
    }

    const adobeFileCompressionLink = '<b><a style="text-decoration: underline; color: blue;" href="https://www.adobe.com/acrobat/online/compress-pdf.html"'
        + 'target="_blank"' + ' >Adobe File Compression Tool</a></b>'

    const handleChange = async function(event: any) {

        const file: File | undefined = event.target.files[0];
        if (!file) { alert("Please select a file."); }

        if (file) {
            if (file.size > 100000000) {
                if (file.type == "application/pdf") {
                    Swal.fire('The PDF File Is Too Large',
                        'Please consider using the following ' + adobeFileCompressionLink + ' to compress your file.',
                        'error');
                } else {
                    Swal.fire('File Too Large', 'Please upload a smaller file.', 'error');
                }
            }
            else {
                openFileRef.current.value = "";
                // Return back to the caller for processing
                await handleUploadFile(file);
            }
        }

    }

    

    useEffect(() => {
        setRevenueDocumentation([]);
        if (!currentAfr) return;
        if (!currentAfr.currentversion) return;

        // get state from redux
        var newLocalState = generateLocalStateFromEventProperties(currentAfr, ["workState_revenuesComplete", "workState_noRevenues", "reviewState_revenuesReviewed"])

        // enable/disable checkbox when there are (no) facts 
        setHasFacts(existFactsInTable(currentAfr, ["uas_020_revenue", "uas_020_revenue_dwellings_and_fees"]))
        var tbl = getTable(currentAfr, "uas_020_revenue");
        if (tbl && tbl.structure) {
            setRevenueDocumentation(tbl.structure.documentationByYear.split(/<br\/>/g));
        }
        // create a local state from redux
        setState(newLocalState)

    }, [currentAfr, currVersion()])

    useEffect(() => {

        dispatch(resetConceptState({}));

    }, [activeTab])

    useEffect(() => {
        if (parameters.state && parameters.state.tabId && parameters.state.tabId.length > 0) {
            toggle(parameters.state.tabId);
        }

    }, [parameters.state])

    return (
        <React.Fragment>
            <Page>
                <h2>Revenues for {utilityHelper.getEntityName(entity, currentAfr)} ({utilityHelper.getEntityCode(entity, currentAfr)}), {afrYear}</h2>
                <Row>
                    <Col>
                        <div className="box-events pl-2 pr-3 py-3 my-2">
                            <div>
                                <Label className="ml-4" check>
                                    <Input type="checkbox"
                                        name="workState_noRevenues"
                                        onChange={handleChangeCheckbox}
                                        disabled={hasFacts || utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified)
                                            || (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin)}
                                        checked={state["workState_noRevenues"] ? true : false}
                                    />{' '} <b>No Revenues</b>
                                </Label>
                                <Label className="ml-5" check>
                                    <Input type="checkbox"
                                        name="workState_revenuesComplete"
                                        onChange={handleChangeCheckbox}
                                        disabled={!(hasFacts || (state["workState_noRevenues"] ? true : false)) ||
                                            (state["workState_revenuesComplete"] ? utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified) : false)
                                            || (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin) }
                                        checked={state["workState_revenuesComplete"] ? true : false}
                                    />{' '} <b>Done</b>
                                </Label>
                                {isAdminUser && (
                                    <Label className="ml-5" check>
                                        <Input type="checkbox"
                                            name="reviewState_revenuesReviewed"
                                            onChange={handleChangeCheckbox}
                                            checked={state["reviewState_revenuesReviewed"] ? true : false}
                                            disabled={!utilityHelper.checkStatusList(currentAfr, enums.certifiedRecertified) }
                                        /><b>Reviewed</b>
                                    </Label>)}
                            </div>
                        </div>
                        <div>
                            <Nav tabs>
                                <NavItem>
                                    <NavLink
                                        className={activeClass('1')}
                                        onClick={() => { toggle('1'); }}
                                    >
                                        Instructions
                                    </NavLink>
                                </NavItem>
                                {utilityHelper.showTab(currentAfr, enums.tableName.revenues) &&
                                <NavItem>
                                    <NavLink
                                        className={activeClass('2')}
                                        onClick={() => { toggle('2'); }}
                                    >
                                        Revenues
                                    </NavLink>
                                </NavItem>}
                                {utilityHelper.showTab(currentAfr, enums.tableName.impactfees) &&
                                    <NavItem>
                                        <NavLink
                                            className={activeClass('3')}
                                            onClick={() => { toggle('3'); }}
                                        >
                                            Impact Fees
                                        </NavLink>
                                    </NavItem>}
                            </Nav>
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="1">
                                    <Row>
                                        <Col sm="12">
                                            {revenueDocumentation.length === 0
                                                ?
                                                <h5>
                                                    <p>Use this screen to enter revenue balances by account code and fund group. Use the box to the right to add, delete, and import lines of revenue data. To update an existing line of revenue data, select the icon in the edit column beside the appropriate row. Fields will only allow whole numbers (no decimals).</p>
                                                    <p>HB 1339 mandated that additional information be reported regarding impact fees. These requirements are outlined in 163.31801(11), F.S.In addition to the specific purpose of the impact fee, which is reported via the Revenue Account Code series 324.xxx, the impact fees must now be reported with a corresponding Dwelling Code and Schedule Policy Code. These codes will be used to determine the amount assessed for each purpose, each type of dwelling, and the schedule policy describing the method of calculating impact fees. For specific information regarding the definitions and use of Dwelling Codes and Schedule Policy Codes, please review the applicable Uniform Account System(UAS) Manual for the AFR reporting year located here. Local government entities must also provide each exception or waiver for construction or development of affordable housing. In order to comply with this statutory requirement, please upload a single PDF packet of applicable waivers and a single PDF packet of applicable exceptions using the “Upload Exceptions or Waivers” button. These documents will be stored under the Documents tab on the AFR Summary Screen. If you do not have any exceptions or waivers to provide, you must select “No applicable exceptions or waivers”. You will not be allowed to check the “Done Entering Revenues” checkbox until you have either uploaded the exceptions or waivers or selected the “No applicable exceptions or waivers” checkbox.</p>
                                                    <p>Before the system will allow you to submit your AFR, you must check the “Done Entering Revenues” checkbox. This is still true if you have checked the “No Revenues” checkbox. If the “Done Entering Revenues” checkbox is not checked at the time you submit the AFR, you will receive an error message and the AFR will not submit.</p>
                                                </h5>
                                                :
                                                <h5>
                                                    {revenueDocumentation.map((option : string, idx) => (
                                                        <div key={"revdoc" + idx} dangerouslySetInnerHTML={{__html:"<p>" + option + "</p>"}} ></div>
                                                    ))}
                                                </h5>
                                            }
                                        </Col>
                                    </Row>
                                </TabPane>
                                <TabPane tabId="2">
                                    <Row>
                                        <Col sm="12">
                                            {(activeTab === "2") && (<RevenuesCoreDetail doneFlag={"workState_revenuesComplete"} noFlag={"workState_noRevenues"} setHasData={updateHasFacts} fundGroup={null} />)}
                                        </Col>
                                    </Row>
                                </TabPane>
                                <TabPane tabId="3">
                                    <Row>
                                        <Col sm="12">
                                            {(activeTab === "3") && (<RevenuesCoreImpactFees doneFlag={"workState_revenuesComplete"} noFlag={"workState_noRevenues"} setHasData={updateHasFacts} fundGroup={null} />)}
                                            <div hidden={true}>
                                                <input
                                                    ref={openFileRef}
                                                    type="file"
                                                    style={{ display: 'none' }}
                                                    accept={ ".PDF"}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                </TabPane>
                            </TabContent>
                        </div>

                    </Col>
                </Row>
            </Page>
        </React.Fragment>
    );
}

export default RevenuesCore;